import type { I18nDescriptor } from '~/scopes/campaign/data/metals/types'

import translations from './sources.translations'

/** A citable entry as it prints: the client's wording, and its number when it has one. */
export interface Reference {
  number?: number
  label: I18nDescriptor
}

/** A reference as used in one place — `note` is that section's "Role: …" clause. */
export interface Citation extends Reference {
  note?: I18nDescriptor
}

export interface Source extends Reference {
  /**
   * The entry's number in the client's official source list. Printed as-is rather than
   * renumbered per metal, so a study keeps one identity everywhere: `[3]` is ADEME on
   * every metal page and in the client's own source document, which lists the studies in
   * this order with no logic of its own — these numbers are the contract between it and
   * the app.
   *
   * A metal's markers therefore read out of order (cobalt cites 3 before 9) and index no
   * local list — each one opens the whole bibliography (`?view=sources`).
   */
  number: number
}

/**
 * The campaign's shared bibliography — the client's official source list, authored once
 * and cited by every metal that needs it.
 *
 * Keyed by slug rather than by position: a metal cites only a handful of the entries, and
 * authored copy should not have to change when the list grows. `number` carries the
 * official identity instead.
 *
 * `label` is the client's wording verbatim, with two mechanical concessions to a
 * single-line list: entries authored across several lines are joined with spaces, and
 * bare URLs are wrapped as `[url](url)` — `InlineMarkdown` linkifies that form only, and
 * the visible text is unchanged either way.
 *
 * The whole list is transcribed, not just what is cited today: it renders whole, and
 * authoring a metal means pointing at a key rather than re-transcribing a study. The
 * cost is that every entry ships a translatable string.
 */
export const SOURCES = {
  usgsMcs2025: {
    number: 1,
    label: translations.usgsMcs2025Label,
  },
  gomez2023: {
    number: 2,
    label: translations.gomez2023Label,
  },
  ademe2024: {
    number: 3,
    label: translations.ademe2024Label,
  },
  ieaGcmo2025: {
    number: 4,
    label: translations.ieaGcmo2025Label,
  },
  brunningSmartphone2023: {
    number: 5,
    label: translations.brunningSmartphone2023Label,
  },
  amnesty2016: {
    number: 6,
    label: translations.amnesty2016Label,
  },
  // The client's §3 calls [7] GreenIT, but this list — which every marker indexes — gives
  // it to the IEA explorer and GreenIT to [30]. Flagged; §4 wins.
  ieaMineralsExplorer: {
    number: 7,
    label: translations.ieaMineralsExplorerLabel,
  },
  ieaRecycling2024: {
    number: 8,
    label: translations.ieaRecycling2024Label,
  },
  pblEnvironmental2024: {
    number: 9,
    label: translations.pblEnvironmental2024Label,
  },
  pblSocial2025: {
    number: 10,
    label: translations.pblSocial2025Label,
  },
  unicefDrc2022: {
    number: 11,
    label: translations.unicefDrc2022Label,
  },
  unepToxicTrail2023: {
    number: 12,
    label: translations.unepToxicTrail2023Label,
  },
  wwfGold2021: {
    number: 13,
    label: translations.wwfGold2021Label,
  },
  unepTradingMercury: {
    number: 14,
    label: translations.unepTradingMercuryLabel,
  },
  unepAsgm: {
    number: 15,
    label: translations.unepAsgmLabel,
  },
  yaleE360Myanmar2025: {
    number: 16,
    label: translations.yaleE360Myanmar2025Label,
  },
  bhrrc2023: {
    number: 17,
    label: translations.bhrrc2023Label,
  },
  // The client's list mangles this link — the visible text is cut at `…CELEX%3A320`
  // with a stray `24R1252` trailing outside it. Both halves are the same OJ record,
  // so it is written whole rather than reproducing the copy-paste artifact.
  euCrma2024: {
    number: 18,
    label: translations.euCrma2024Label,
  },
  jrcCircular2017: {
    number: 19,
    label: translations.jrcCircular2017Label,
  },
  pulitzerTinIslands2025: {
    number: 20,
    label: translations.pulitzerTinIslands2025Label,
  },
  bgrTin2020: {
    number: 21,
    label: translations.bgrTin2020Label,
  },
  afrewatchDeforestation2026: {
    number: 22,
    label: translations.afrewatchDeforestation2026Label,
  },
  cikuruKamera2026: {
    number: 23,
    label: translations.cikuruKamera2026Label,
  },
  bgrTantalum2021: {
    number: 24,
    label: translations.bgrTantalum2021Label,
  },
  beiserPowerMetal2024: {
    number: 25,
    label: translations.beiserPowerMetal2024Label,
  },
  amnestyLithium2026: {
    number: 26,
    label: translations.amnestyLithium2026Label,
  },
  jrcBauxite2022: {
    number: 27,
    label: translations.jrcBauxite2022Label,
  },
  hrwGuinea2018: {
    number: 28,
    label: translations.hrwGuinea2018Label,
  },
  linGalliumArsenide2026: {
    number: 29,
    label: translations.linGalliumArsenide2026Label,
  },
  greenIt: {
    number: 30,
    label: translations.greenItLabel,
  },
  liuIndium2022: {
    number: 31,
    label: translations.liuIndium2022Label,
  },
  iucnGorilla2019: {
    number: 32,
    label: translations.iucnGorilla2019Label,
  },
  ipisDrc2022: {
    number: 33,
    label: translations.ipisDrc2022Label,
  },
  transparencyMadagascar2025: {
    number: 34,
    label: translations.transparencyMadagascar2025Label,
  },
  wgcRecycling: {
    number: 35,
    label: translations.wgcRecyclingLabel,
  },
  chenManganese2026: {
    number: 36,
    label: translations.chenManganese2026Label,
  },
  atsdrNickel: {
    number: 37,
    label: translations.atsdrNickelLabel,
  },
  hrwKabwe2025: {
    number: 38,
    label: translations.hrwKabwe2025Label,
  },
  zhangLeadZinc2012: {
    number: 39,
    label: translations.zhangLeadZinc2012Label,
  },
  chenSilica2012: {
    number: 40,
    label: translations.chenSilica2012Label,
  },
  bolanAntimony2022: {
    number: 41,
    label: translations.bolanAntimony2022Label,
  },
  zhaoAntimony2023: {
    number: 42,
    label: translations.zhaoAntimony2023Label,
  },
  yeAntimony2018: {
    number: 43,
    label: translations.yeAntimony2018Label,
  },
  schildrothAntimony2020: {
    number: 44,
    label: translations.schildrothAntimony2020Label,
  },
  scrreenSilver2023: {
    number: 45,
    label: translations.scrreenSilver2023Label,
  },
  unepMercuryGeneral: {
    number: 46,
    label: translations.unepMercuryGeneralLabel,
  },
  silverInstitute2025: {
    number: 47,
    label: translations.silverInstitute2025Label,
  },
  silverInstitute2026: {
    number: 48,
    label: translations.silverInstitute2026Label,
  },
  scrreenGermanium2023: {
    number: 49,
    label: translations.scrreenGermanium2023Label,
  },
  scrreenMagnesium2023: {
    number: 50,
    label: translations.scrreenMagnesium2023Label,
  },
  wangMagnesium2020: {
    number: 51,
    label: translations.wangMagnesium2020Label,
  },
  bgrPgm2022: {
    number: 52,
    label: translations.bgrPgm2022Label,
  },
  scrreenPgm2023: {
    number: 53,
    label: translations.scrreenPgm2023Label,
  },
  scrreenArsenic2025: {
    number: 54,
    label: translations.scrreenArsenic2025Label,
  },
  scrreenBarytes2025: {
    number: 55,
    label: translations.scrreenBarytes2025Label,
  },
  qyBariumTitanate2026: {
    number: 56,
    label: translations.qyBariumTitanate2026Label,
  },
  scrreenBorate2025: {
    number: 57,
    label: translations.scrreenBorate2025Label,
  },
  unepRecyclingRates2011: {
    number: 58,
    label: translations.unepRecyclingRates2011Label,
  },
  scrreenCadmium2025: {
    number: 59,
    label: translations.scrreenCadmium2025Label,
  },
  itaiItaiDisease: {
    number: 60,
    label: translations.itaiItaiDiseaseLabel,
  },
  euRohs2003: {
    number: 61,
    label: translations.euRohs2003Label,
  },
  scrreenChromium2025: {
    number: 62,
    label: translations.scrreenChromium2025Label,
  },
  scrreenHafnium2025: {
    number: 63,
    label: translations.scrreenHafnium2025Label,
  },
  matosMsa2021: {
    number: 64,
    label: translations.matosMsa2021Label,
  },
  scrreenIron2025: {
    number: 65,
    label: translations.scrreenIron2025Label,
  },
  scrreenLead2025: {
    number: 66,
    label: translations.scrreenLead2025Label,
  },
  scrreenNiobium2025: {
    number: 67,
    label: translations.scrreenNiobium2025Label,
  },
  scrreenPotash2025: {
    number: 68,
    label: translations.scrreenPotash2025Label,
  },
  scrreenStrontium2025: {
    number: 69,
    label: translations.scrreenStrontium2025Label,
  },
  scrreenTellurium2025: {
    number: 70,
    label: translations.scrreenTellurium2025Label,
  },
  scrreenTitanium2025: {
    number: 71,
    label: translations.scrreenTitanium2025Label,
  },
  scrreenVanadium2025: {
    number: 72,
    label: translations.scrreenVanadium2025Label,
  },
  scrreenZirconium2025: {
    number: 73,
    label: translations.scrreenZirconium2025Label,
  },
} as const satisfies Record<string, Source>

export type SourceKey = keyof typeof SOURCES

/** Official order, so a metal's bibliography reads the same way as the source list. */
export const SOURCE_ORDER = (Object.keys(SOURCES) as SourceKey[]).sort(
  (a, b) => SOURCES[a].number - SOURCES[b].number,
)

/**
 * The client's trailing, unnumbered group: "sources used in scoring methodology only".
 * Outside `SOURCES` so that a metal sheet citing one — they have no number to print — is a
 * type error rather than a review note.
 */
export const METHOD_SOURCES = {
  graedel2015: { label: translations.graedel2015Label },
  undpHdi: { label: translations.undpHdiLabel },
  worldBankWgi: { label: translations.worldBankWgiLabel },
  bgsWorldMineralProduction: {
    label: translations.bgsWorldMineralProductionLabel,
  },
  cambridgeMechatronics: { label: translations.cambridgeMechatronicsLabel },
} as const satisfies Record<string, Reference>

/** The group in the client's order, which is theirs to set — nothing is sorted here. */
export const METHOD_SOURCE_ORDER: readonly Reference[] =
  Object.values(METHOD_SOURCES)

/**
 * The trailing superscript for a block's citations, e.g. `"4,10,25"` — the official
 * numbers, ascending. Empty when the block cites nothing, which is how `SourceMarker`
 * gates its own `<sup>`.
 */
export const sourceMarker = (refs?: readonly SourceKey[]) =>
  (refs ?? [])
    .map((key) => SOURCES[key].number)
    .sort((a, b) => a - b)
    .join(',')
